import {
  SiBehance, SiBehanceHex, SiDiscord, SiDiscordHex, SiDribbble, SiDribbbleHex,
  SiFacebook, SiFacebookHex, SiGithub, SiInstagram, SiInstagramHex,
  SiMedium, SiPinterest, SiPinterestHex, SiReddit, SiRedditHex, SiSnapchat,
  SiSoundcloud, SiSoundcloudHex, SiSpotify, SiSpotifyHex, SiTelegram,
  SiTelegramHex, SiThreads, SiTiktok, SiTwitch, SiTwitchHex, SiVimeo,
  SiVimeoHex, SiWhatsapp, SiWhatsappHex, SiX, SiYoutube, SiYoutubeHex,
} from '@icons-pack/react-simple-icons'
import { Globe } from 'lucide-react'

/**
 * Brand slug to icon.
 *
 * From the package rather than the CDN the design mock uses: `cdn.simpleicons.org`
 * is a third-party request on every page load, and the icons are already
 * installed here. `optimizePackageImports` rewrites this barrel import to one
 * import per icon, so listing twenty-one of them costs twenty-one of them.
 *
 * LinkedIn is deliberately absent: Simple Icons dropped it over a trademark
 * request, so it falls through to the globe like any other unlisted brand.
 *
 * Unknown slugs fall back to a globe. Users type their own brand names and the
 * list will always be behind them, so an unrecognised one has to render as a
 * link rather than as a hole in the row.
 */
export const SOCIAL_ICON: Record<string, typeof Globe> = {
  behance: SiBehance,
  discord: SiDiscord,
  dribbble: SiDribbble,
  facebook: SiFacebook,
  github: SiGithub,
  instagram: SiInstagram,
  medium: SiMedium,
  pinterest: SiPinterest,
  reddit: SiReddit,
  snapchat: SiSnapchat,
  soundcloud: SiSoundcloud,
  spotify: SiSpotify,
  telegram: SiTelegram,
  threads: SiThreads,
  tiktok: SiTiktok,
  twitch: SiTwitch,
  vimeo: SiVimeo,
  whatsapp: SiWhatsapp,
  x: SiX,
  youtube: SiYoutube,
}

/** The brands offered as one-tap chips, in the order Mongolian creators use them. */
export const SUGGESTED_BRANDS = [
  'instagram', 'facebook', 'tiktok', 'youtube', 'x',
  'telegram', 'spotify', 'threads', 'github', 'whatsapp',
]

/**
 * Everything the picker offers, suggested ones first.
 *
 * A few of these have no icon in the package and fall through to the globe.
 * LinkedIn is the notable one: Simple Icons dropped it over a trademark
 * request, and leaving the most-asked-for professional network out of the list
 * because of that would be letting a packaging detail decide the product.
 *
 * `website` is the escape hatch, and it takes a plain URL rather than a handle.
 */
export const SOCIAL_BRANDS = [
  ...SUGGESTED_BRANDS,
  'linkedin', 'discord', 'twitch', 'soundcloud', 'pinterest',
  'snapchat', 'reddit', 'behance', 'dribbble', 'medium', 'vimeo',
  'website',
]

export function socialIcon(brand: string) {
  return SOCIAL_ICON[brand?.toLowerCase()] ?? Globe
}

/**
 * Brands that write their own name with a capital in the middle.
 *
 * Only the exceptions are listed. Everything else is the slug with a capital on
 * the front, which is what these are too, in the sense that matters: a label
 * sitting beside the TikTok logo reading `Tiktok` is a spelling mistake with a
 * picture of the correct spelling next to it.
 */
const SOCIAL_NAMES: Record<string, string> = {
  tiktok: 'TikTok',
  youtube: 'YouTube',
  github: 'GitHub',
  whatsapp: 'WhatsApp',
  linkedin: 'LinkedIn',
  soundcloud: 'SoundCloud',
}

/**
 * Official mark colours, taken from the icon package rather than typed out, so
 * a colour cannot drift from the icon it is painted onto.
 *
 * Deliberately not every brand. X, GitHub, Threads, TikTok and Medium all
 * publish their mark as black, and Snapchat publishes its as `#FFFC00`; a
 * black icon on a dark card and a yellow one on a light card are both just an
 * empty tile. Those brands are left out and follow the theme's text colour
 * instead, which is how their own apps draw them too.
 */
const SOCIAL_HEX: Record<string, string> = {
  instagram: SiInstagramHex,
  facebook: SiFacebookHex,
  youtube: SiYoutubeHex,
  telegram: SiTelegramHex,
  spotify: SiSpotifyHex,
  whatsapp: SiWhatsappHex,
  discord: SiDiscordHex,
  twitch: SiTwitchHex,
  soundcloud: SiSoundcloudHex,
  pinterest: SiPinterestHex,
  reddit: SiRedditHex,
  behance: SiBehanceHex,
  dribbble: SiDribbbleHex,
  vimeo: SiVimeoHex,
}

/**
 * The colour to draw a brand's icon in, or undefined to leave it on the theme.
 *
 * Undefined rather than a fallback colour on purpose: `style={{ color:
 * undefined }}` is a no-op, so an uncoloured brand keeps whatever the class
 * list gives it, hover included.
 */
export function socialColor(brand: string): string | undefined {
  return SOCIAL_HEX[brand?.toLowerCase()]
}

/**
 * A brand as it is shown to somebody. Display only: the slug is what is stored,
 * and it is what every lookup here is keyed on.
 *
 * Not translated, and not a message key. These are proper nouns, and they are
 * written the same way in every language this app ships in.
 */
export function socialName(brand: string): string {
  const slug = brand?.toLowerCase() ?? ''
  if(!slug) return ''
  return SOCIAL_NAMES[slug] ?? slug.charAt(0).toUpperCase() + slug.slice(1)
}


/*  ============================================================================
    Handles and links

    What gets stored is always a full URL, because that is what the row renders
    as an href. What gets typed is almost always a handle, because that is what
    people know about themselves. These turn one into the other.
    ========================================================================= */

interface SocialProfile {
  /** Where a profile lives, minus the handle: `t.me/`, `tiktok.com/@`. */
  prefix: string
  /**
   * Other hosts that mean the same brand. A link is pasted from wherever the
   * person happened to be, which is `m.facebook.com` as often as not, and one
   * that is not recognised silently stops collapsing to a handle.
   */
  hosts?: string[]
}

const SOCIAL_PROFILES: Record<string, SocialProfile> = {
  instagram: { prefix: 'instagram.com/' },
  facebook: { prefix: 'facebook.com/', hosts: ['facebook.com', 'fb.com', 'fb.me'] },
  tiktok: { prefix: 'tiktok.com/@' },
  youtube: { prefix: 'youtube.com/@', hosts: ['youtube.com', 'youtu.be'] },
  x: { prefix: 'x.com/', hosts: ['x.com', 'twitter.com'] },
  telegram: { prefix: 't.me/', hosts: ['t.me', 'telegram.me'] },
  spotify: { prefix: 'open.spotify.com/user/', hosts: ['open.spotify.com', 'spotify.com'] },
  threads: { prefix: 'threads.com/@', hosts: ['threads.com', 'threads.net'] },
  github: { prefix: 'github.com/' },
  whatsapp: { prefix: 'wa.me/', hosts: ['wa.me', 'whatsapp.com', 'api.whatsapp.com'] },
  linkedin: { prefix: 'linkedin.com/in/' },
  discord: { prefix: 'discord.gg/', hosts: ['discord.gg', 'discord.com'] },
  twitch: { prefix: 'twitch.tv/' },
  soundcloud: { prefix: 'soundcloud.com/' },
  pinterest: { prefix: 'pinterest.com/' },
  snapchat: { prefix: 'snapchat.com/add/' },
  reddit: { prefix: 'reddit.com/user/' },
  behance: { prefix: 'behance.net/' },
  dribbble: { prefix: 'dribbble.com/' },
  medium: { prefix: 'medium.com/@' },
  vimeo: { prefix: 'vimeo.com/' },
}

/** Subdomains that are the same site. Dropped before a host is compared. */
const MIRRORS = /^(?:www|m|web|mobile)\./

/**
 * Whether what somebody typed names a host.
 *
 * The trailing slash is what makes this safe. A hostname and a handle are the
 * same shape otherwise, and plenty of real handles have a dot in them, so
 * `sanchir.2011` has to stay a handle while `instagram.com/sanchir` is a link.
 */
function looksLikeUrl(value: string): boolean {
  return /^[a-z][a-z0-9+.-]*:\/\//i.test(value) || /^[\w-]+(?:\.[\w-]+)+\//.test(value)
}

function withScheme(value: string): string {
  return /^https?:\/\//i.test(value) ? value : `https://${value}`
}

function split(profile: SocialProfile): { hosts: string[], path: string } {
  const [host, ...rest] = profile.prefix.split('/')
  return {
    hosts: (profile.hosts ?? [host]).map(one => one.replace(MIRRORS, '').toLowerCase()),
    path: `/${rest.join('/')}`,
  }
}

/**
 * What the field shows for a stored link.
 *
 * A handle where the link is a plain profile, and the link itself where it is
 * not. Not every page a brand has is shaped like a handle: a Spotify artist, a
 * YouTube channel id and a subreddit are all real things to link to, and
 * showing `artist/4gzpq5DP` as though it were a username would be a lie that
 * breaks the moment it is saved back.
 */
export function socialHandle(brand: string, url: string): string {
  const value = (url ?? '').trim()
  const profile = SOCIAL_PROFILES[brand]
  if(!value || !profile) return value

  let parsed
  try { parsed = new URL(withScheme(value)) } catch { return value }

  const { hosts, path } = split(profile)
  if(!hosts.includes(parsed.hostname.replace(MIRRORS, '').toLowerCase())) return value

  let pathname = parsed.pathname
  try { pathname = decodeURIComponent(pathname) } catch { /* keep it encoded */ }
  if(!pathname.startsWith(path)) return value

  // One segment and no deeper: anything else is a page, not a person.
  const handle = pathname.slice(path.length).replace(/\/+$/, '')
  return !handle || handle.includes('/') ? value : handle
}

/**
 * The link for what somebody typed.
 *
 * A pasted link is reduced to its handle and rebuilt, so that the same profile
 * is stored identically however it arrived: `m.facebook.com/x/` typed by hand
 * and `x` pasted out of the address bar are the same person, and keeping the
 * two apart only makes the row of icons inconsistent with itself.
 *
 * The leading `@` people write in front of a handle is dropped, since it is not
 * part of it. Where the brand's own address carries one, as TikTok's and
 * YouTube's do, the prefix supplies it.
 *
 * A link with no handle in it survives as a link, because there is nothing to
 * rebuild it from. So does anything under a brand with no known shape.
 */
export function socialUrl(brand: string, typed: string): string {
  const value = (typed ?? '').trim()
  if(!value) return ''

  const profile = SOCIAL_PROFILES[brand]
  if(!profile) return withScheme(value)

  let handle = looksLikeUrl(value) ? socialHandle(brand, value) : value.replace(/^@+/, '').trim()
  // WhatsApp addresses a person by phone number, and `wa.me` wants the digits
  // alone: the `+`, the spaces and the dashes people write a number with all
  // break the link rather than being ignored.
  if(brand === 'whatsapp' && !looksLikeUrl(handle)) handle = handle.replace(/[^\d]/g, '')
  // Nothing left once the decoration comes off. Returning the bare prefix here
  // would be a link to the service's front page dressed up as a link to a
  // person, and it would pass any check that only asks whether the field was
  // filled in.
  if(!handle) return ''
  return looksLikeUrl(handle) ? withScheme(handle) : `https://${profile.prefix}${handle}`
}

/**
 * Which brand a pasted link belongs to, or null when nothing recognises it.
 *
 * The point of this is that a link already says which network it is, so asking
 * somebody to pick the brand *and* paste the link is asking them to tell us the
 * same thing twice. Anything unrecognised returns null rather than guessing, and
 * the caller keeps whatever the person had already chosen.
 *
 * Matched on host alone. The path is what distinguishes a profile from a post,
 * and that is `socialHandle`'s job; this only has to answer "which service".
 */
export function socialBrandFromUrl(typed: string): string | null {
  const value = (typed ?? '').trim()
  if(!value || !looksLikeUrl(value)) return null

  let host
  try { host = new URL(withScheme(value)).hostname.replace(MIRRORS, '').toLowerCase() } catch { return null }

  for(const [brand, profile] of Object.entries(SOCIAL_PROFILES)) {
    if(split(profile).hosts.includes(host)) return brand
  }
  return null
}

/**
 * Whether what somebody typed will resolve to a real profile link.
 *
 * Used to tell them before they save rather than after: an empty field and a
 * field holding `@` are both "nothing to link to", and only one of them looks
 * like it has been filled in.
 */
export function isSocialComplete(brand: string, typed: string): boolean {
  return !!socialUrl(brand, typed)
}

/**
 * The greyed-out `instagram.com/` in front of the field, or null where there is
 * nothing to put there: a brand with no known shape, or a field that currently
 * holds a whole link rather than a handle.
 */
export function socialPrefix(brand: string, typed: string): string | null {
  const profile = SOCIAL_PROFILES[brand]
  if(!profile || looksLikeUrl((typed ?? '').trim())) return null
  return profile.prefix
}
